import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { authApi, IMPERSONATION_ADMIN_TOKEN_KEY, IMPERSONATION_ADMIN_USER_KEY, restoreToken } from '../services/api'

interface User {
    // Личная почта для OTP: на неё приходят коды входа.
    wiseway_email?: string | null
    id: number
    username: string
    full_name: string | null
    role: string
    is_active: boolean
    avatar_url?: string | null
    // Скрыты ли от пользователя контакты лидов: приходит с сервера, влияет на
    // подписи и подсказки там, где лид ещё не загружен.
    contacts_hidden?: boolean
    // Что пользователю разрешено делать с карточками лидов.
    can_edit_leads?: boolean
    can_delete_leads?: boolean
    can_block_leads?: boolean
    // Разделы, доступные пользователю.
    can_view_dashboard?: boolean
    can_view_queue?: boolean
    can_view_lead_cards?: boolean
    can_view_duplicates?: boolean
    mark_read_on_reply_only?: boolean
    folder_unread_badges?: boolean
    block_manual_lead_create?: boolean
    crm_id_only_lead_create?: boolean
    can_create_lead_from_chat?: boolean
    // Профили видимости сотрудника и действует ли для него видимость по ним.
    visibility_profile_ids?: number[]
    visibility_profile_names?: string[]
    visibility_profiles_enabled?: boolean
    // «Админ разработки», который сейчас работает под этим сотрудником.
    impersonated_by_id?: number | null
    impersonated_by_name?: string | null
    // Права роли и статусы CRM, которые она может выставить сама.
    permissions?: string[]
    allowed_statuses?: string[]
    role_label?: string | null
}

// Вход по коду из письма: код отправлен, его нужно ввести.
export interface OtpChallenge {
    challengeId: string
    sentTo: string | null
    resendIn: number
}

// Ключи запомненного браузера по логинам: сутки после ввода кода сервер его
// не спрашивает. Хранится только в этом браузере.
const TRUSTED_DEVICES_KEY = 'trusted_login_devices'

function readTrustedDevices(): Record<string, string> {
    try {
        return JSON.parse(localStorage.getItem(TRUSTED_DEVICES_KEY) || '{}') || {}
    } catch {
        return {}
    }
}

function rememberTrustedDevice(username: string, token: string) {
    try {
        const devices = readTrustedDevices()
        devices[username.toLowerCase()] = token
        localStorage.setItem(TRUSTED_DEVICES_KEY, JSON.stringify(devices))
    } catch {
        // Без хранилища код просто будет спрашиваться при каждом входе.
    }
}

interface AuthState {
    token: string | null
    user: User | null
    isAuthenticated: boolean
    isLoading: boolean
    error: string | null
    // Свежий профиль (с актуальными переключателями «Доп. функций») уже
    // получен в этой сессии. До этого persist мог отдать устаревший user из
    // localStorage - код, зависящий от таких флагов, должен подождать.
    profileLoaded: boolean

    // Возвращает данные кода, если после пароля нужен код из письма.
    login: (username: string, password: string) => Promise<OtpChallenge | null>
    verifyOtp: (username: string, challengeId: string, code: string) => Promise<void>
    resendOtp: (challengeId: string) => Promise<OtpChallenge>
    logout: () => void
    fetchUser: () => Promise<void>
    // Вход «Админа разработки» под сотрудником и возврат в свою учётку.
    impersonate: (operatorId: number) => Promise<void>
    stopImpersonation: () => void
    clearError: () => void
}

export const useAuthStore = create<AuthState>()(
    persist(
        (set, get) => ({
            token: null,
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
            profileLoaded: false,

            login: async (username: string, password: string) => {
                set({ isLoading: true, error: null })
                try {
                    const deviceToken = readTrustedDevices()[username.toLowerCase()] || null
                    const response = await authApi.login(username, password, deviceToken)
                    const { access_token, otp_required, challenge_id, sent_to, resend_in } = response.data

                    if (otp_required) {
                        set({ isLoading: false })
                        return { challengeId: challenge_id, sentTo: sent_to || null, resendIn: resend_in || 0 }
                    }

                    localStorage.setItem('token', access_token)
                    set({ token: access_token, isAuthenticated: true })


                    await get().fetchUser()

                    set({ isLoading: false })
                    return null
                } catch (error: any) {
                    const message = error.response?.data?.detail || 'Ошибка входа'
                    set({ isLoading: false, error: message })
                    throw error
                }
            },

            verifyOtp: async (username: string, challengeId: string, code: string) => {
                set({ isLoading: true, error: null })
                try {
                    const response = await authApi.verifyOtp(challengeId, code)
                    const { access_token, device_token } = response.data
                    if (device_token) rememberTrustedDevice(username, device_token)

                    localStorage.setItem('token', access_token)
                    set({ token: access_token, isAuthenticated: true })
                    await get().fetchUser()
                    set({ isLoading: false })
                } catch (error: any) {
                    const message = error.response?.data?.detail || 'Не удалось проверить код'
                    set({ isLoading: false, error: message })
                    throw error
                }
            },

            resendOtp: async (challengeId: string) => {
                set({ error: null })
                try {
                    const response = await authApi.resendOtp(challengeId)
                    const { challenge_id, sent_to, resend_in } = response.data
                    return { challengeId: challenge_id, sentTo: sent_to || null, resendIn: resend_in || 0 }
                } catch (error: any) {
                    const message = error.response?.data?.detail || 'Не удалось отправить код'
                    set({ error: message })
                    throw error
                }
            },

            logout: () => {
                localStorage.removeItem('token')
                set({
                    token: null,
                    user: null,
                    isAuthenticated: false,
                    error: null,
                })
            },

            fetchUser: async () => {
                try {
                    const response = await authApi.getMe()
                    set({ user: response.data, profileLoaded: true })
                } catch (error: any) {
                    // Выходим только если сервер прямо сказал, что вход
                    // недействителен. Лимит nginx (503), перезапуск бэкенда (502)
                    // или сбой сети - не повод выкидывать человека: раньше из-за
                    // них разлогинивало сразу во всех вкладках.
                    if (error?.response?.status === 401) {
                        get().logout()
                    }
                }
            },

            impersonate: async (operatorId: number) => {
                const adminToken = get().token || localStorage.getItem('token')
                const response = await authApi.impersonate(operatorId)
                if (adminToken) localStorage.setItem(IMPERSONATION_ADMIN_TOKEN_KEY, adminToken)
                localStorage.setItem(IMPERSONATION_ADMIN_USER_KEY, JSON.stringify(get().user))
                restoreToken(response.data.access_token)
                // Полная перезагрузка: все разделы и подключение в реальном
                // времени открываются заново уже от имени сотрудника.
                window.location.href = '/'
            },

            stopImpersonation: () => {
                const adminToken = localStorage.getItem(IMPERSONATION_ADMIN_TOKEN_KEY)
                const adminUser = localStorage.getItem(IMPERSONATION_ADMIN_USER_KEY)
                localStorage.removeItem(IMPERSONATION_ADMIN_TOKEN_KEY)
                localStorage.removeItem(IMPERSONATION_ADMIN_USER_KEY)
                if (!adminToken) {
                    get().logout()
                    window.location.href = '/login'
                    return
                }
                restoreToken(adminToken, adminUser ? JSON.parse(adminUser) : null)
                window.location.href = '/admin/operators'
            },

            clearError: () => set({ error: null }),
        }),
        {
            name: 'auth-storage',
            partialize: (state) => ({
                token: state.token,
                user: state.user,
                isAuthenticated: state.isAuthenticated,
            }),
        }
    )
)
